import React, { useEffect, useState } from "react";
import Card from "components/Card";
import Input from "components/Form/Input";
import PageTitle from "components/Title/Page";
import _ from "lodash";
import SpinnerMedium from "components/Spinner/Medium";
import Button from "components/Form/Button";
import { useNavigate } from "react-router-dom";
import Textarea from "components/Form/Textarea";
import KoreanAddressInput, { KoreanAddressType } from "components/Form/KoreanAddressInput";
import SingleImageUploader from "components/Form/Input/Image/SingleUploader";
import MultiImageUploader from "components/Form/Input/Image/MultiUploader";
import EmptyForm from "components/Form/Empty";
import MultiBadgeCombo from "components/Form/MultiBadgeCombo";
import { useModal } from "components/Modal/Simple";
import FileUploadingSpinner from "common/components/FileUploadingSpinner";
import { PublicApis } from "common/apis/public";

const ClinicEditForm = ({ clinicInfo, onSave = async (formData) => { }, hideId = false }) => {
    const [formData, setFormData] = useState(null);
    const [specialtyItems, setSpecialtyItems] = useState([]);
    const { Modal: UploadingModal, openModal: openUploadingModal, closeModal: closeUploadingModal } = useModal();
    const navigate = useNavigate();

    useEffect(() => {
        fetchSpecialties();
    }, []);

    useEffect(() => {
        if (_.isNil(clinicInfo)) {
            return;
        }
        console.log("[ClinicEditForm] clinicInfo", clinicInfo);
        setFormData({
            ...clinicInfo
        })
    }, [clinicInfo])

    const fetchSpecialties = async () => {
        const ret = await PublicApis.getSpecialties();
        setSpecialtyItems(ret.data);
        console.log("[ClinicEditForm] fetchSpecialties", ret);
    }

    const onAddressChanged = (addressObj: KoreanAddressType) => {
        console.log("[ClinicEditPage] onAddressChanged", addressObj);
        setFormData({
            ...formData,
            address: addressObj
        })
    }

    const handleSave = async () => {
        openUploadingModal();
        await onSave(formData);
        closeUploadingModal();
    }

    if (_.isNil(formData)) {
        return (
            <SpinnerMedium />
        )
    }

    console.log("[ClinicEditForm] specialtyItems", specialtyItems);
    return (
        <Card>
            <div className="p-3 flex flex-col space-y-2">
                <PageTitle>의원 정보 수정</PageTitle>
                {!hideId && (<div>
                    <div className="font-semibold">
                        의원 ID
                    </div>
                    <Input
                        value={clinicInfo.id}
                        disabled={true}
                    />
                </div>)}
                <div>
                    <div className="font-semibold">
                        의원명
                    </div>
                    <Input
                        value={formData.name}
                        onChange={(value) => setFormData({ ...formData, name: value })}
                    />
                </div>
                <div>
                    <div className="font-semibold">
                        설명
                    </div>
                    <Textarea
                        value={formData.description}
                        onChange={(value) => setFormData({ ...formData, description: value })}
                        resizable={true}
                        minRows={5}
                    />
                </div>
                <div>
                    <div className="font-semibold">
                        주소
                    </div>
                    <EmptyForm>
                        <div className="p-2">
                            <KoreanAddressInput initialAddress={clinicInfo.address} onChange={onAddressChanged} />
                        </div>
                    </EmptyForm>
                </div>
                <div>
                    <div className="font-semibold">
                        진료과목
                    </div>
                    <EmptyForm>
                        <div className="p-2">
                            <MultiBadgeCombo
                                items={specialtyItems}
                                selectedItems={formData?.specialties}
                                onSelectedItemsChange={(items) => {
                                    console.log("[ClinicEditPage] onSelectedItemsChange", items);
                                    setFormData({
                                        ...formData,
                                        specialties: items,
                                    })
                                }}
                            />
                        </div>
                    </EmptyForm>
                </div>
                <div>
                    <div className="font-semibold">
                        프로필 이미지
                    </div>
                    <EmptyForm>
                        <SingleImageUploader
                            file={formData?.profileImage}
                            onFileSelected={(file) => { setFormData((prev) => ({ ...prev, profileImage: file })) }}
                            onFileChanged={(file) => { setFormData((prev) => ({ ...prev, profileImage: file })) }}
                        />
                    </EmptyForm>
                </div>
                <div>
                    <div className="font-semibold">
                        배너 이미지
                    </div>
                    <EmptyForm>
                        <SingleImageUploader
                            file={formData?.bannerImage}
                            onFileSelected={(file) => { setFormData((prev) => ({ ...prev, bannerImage: file })) }}
                            onFileChanged={(file) => { setFormData((prev) => ({ ...prev, bannerImage: file })) }}
                        />
                    </EmptyForm>
                </div>
                <div>
                    <div className="font-semibold">
                        병원 소개 이미지
                    </div>
                    <EmptyForm>
                        <MultiImageUploader
                            files={formData?.introductionImages}
                            onFileSelected={(files) => { setFormData((prev) => ({ ...prev, introductionImages: [...(prev?.introductionImages ?? []), ...files] })) }}
                            onFileChanged={(files) => { setFormData((prev) => ({ ...prev, introductionImages: files })) }}
                        />
                    </EmptyForm>
                </div>
                <div className="border-t border-gray-300"></div>
                <div>
                    <div className="flex justify-between space-x-2">
                        <div>
                            <Button
                                onButtonClicked={() => { navigate(-1) }}
                            >
                                뒤로가기
                            </Button>
                        </div>
                        <div className="flex space-x-2">
                            <div>
                                <Button
                                    onButtonClicked={() => {
                                        const tempClinicInfo = {
                                            ..._.omit(formData, ["specialtyIds"]),
                                            specialties: formData.specialties,
                                            doctors: clinicInfo.doctors,
                                            events: clinicInfo.events,
                                            reviews: clinicInfo.reviews
                                        }
                                        window.localStorage.setItem("tempClinicInfo", JSON.stringify(tempClinicInfo));
                                        window.open(`/preview/clinic/pv`, "_blank")
                                    }}
                                >
                                    미리보기
                                </Button>
                            </div>
                            <div>
                                <Button
                                    onButtonClicked={handleSave}
                                >
                                    저장
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <UploadingModal>
                <FileUploadingSpinner />
            </UploadingModal>
        </Card>
    )
}

export default ClinicEditForm;